import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debtAPI, DebtSummary } from '../../api/debt';
import { RefreshCw, Users, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

export const DebtList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'supplier' | 'customer'>('supplier');
  const [supplierDebts, setSupplierDebts] = useState<DebtSummary[]>([]);
  const [customerDebts, setCustomerDebts] = useState<DebtSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadData();
  }, [showAll]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [suppliers, customers] = await Promise.all([
        debtAPI.getSupplierDebts(showAll),
        debtAPI.getCustomerDebts(showAll),
      ]);
      setSupplierDebts(suppliers);
      setCustomerDebts(customers);
    } catch (error) {
      toast.error(t('debt.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const totalSupplierDebt = supplierDebts.reduce((sum, d) => sum + d.outstandingAmount, 0);
  const totalCustomerDebt = customerDebts.reduce((sum, d) => sum + d.outstandingAmount, 0);

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('debt.title')}</h1>
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span>{t('debt.showAll')}</span>
          </label>
          <button
            onClick={loadData}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card text-center">
          <div className="flex items-center justify-center mb-2">
            <Building2 className="w-5 h-5 text-orange-500 mr-2" />
            <span className="text-sm text-gray-500">{t('debt.supplierDebt')}</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">
            {formatCurrency(totalSupplierDebt)} đ
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {t('debt.withDebt', {countDebt: supplierDebts.filter(d => d.outstandingAmount > 0).length})}
          </p>
        </div>
        <div className="card text-center">
          <div className="flex items-center justify-center mb-2">
            <Users className="w-5 h-5 text-blue-500 mr-2" />
            <span className="text-sm text-gray-500">{t('debt.customerDebt')}</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {formatCurrency(totalCustomerDebt)} đ
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {t('debt.withDebt', {countDebt: customerDebts.filter(d => d.outstandingAmount > 0).length})}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex border-b mb-4">
          <button
            onClick={() => setActiveTab('supplier')}
            className={`px-4 py-2 font-medium flex items-center ${
              activeTab === 'supplier'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500'
            }`}
          >
            <Building2 className="w-4 h-4 mr-2" />
            {t('nav.suppliers')} ({supplierDebts.length})
          </button>
          <button
            onClick={() => setActiveTab('customer')}
            className={`px-4 py-2 font-medium flex items-center ${
              activeTab === 'customer'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500'
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            {t('nav.customers')} ({customerDebts.length})
          </button>
        </div>

        {/* Supplier Debts Table */}
        {activeTab === 'supplier' && (
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 table-header">{t('debt.entityName')}</th>
                <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                <th className="pb-3 table-header text-right">
                  <div>{t('debt.totalValue')}</div>
                  <div className="text-xs font-normal text-orange-500">({t('trips.purchasePrice')})</div>
                </th>
                <th className="pb-3 table-header text-right">{t('debt.paidAmount')}</th>
                <th className="pb-3 table-header text-right">{t('debt.outstandingAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {supplierDebts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    {t('common.noData')}
                  </td>
                </tr>
              ) : (
                supplierDebts.map((debt) => (
                  <tr key={debt.id} className="border-b last:border-0">
                    <td className="py-3">
                      <button
                        onClick={() => navigate(`/debt/suppliers/${debt.id}`)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                        title={t('debt.viewDetails')}
                      >
                        {debt.name}
                      </button>
                    </td>
                    <td className="py-3 text-right">{debt.tripCount}</td>
                    <td className="py-3 text-right">{formatCurrency(debt.totalValue)} đ</td>
                    <td className="py-3 text-right text-green-600">
                      {formatCurrency(debt.paidAmount)} đ
                    </td>
                    <td className={`py-3 text-right font-semibold ${
                      debt.outstandingAmount > 0 ? 'text-orange-600' : 'text-gray-400'
                    }`}>
                      {formatCurrency(debt.outstandingAmount)} đ
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {supplierDebts.length > 0 && (
              <tfoot className="border-t bg-gray-50">
                <tr>
                  <td className="py-3 font-semibold">{t('common.total')}</td>
                  <td className="py-3 text-right font-semibold">
                    {supplierDebts.reduce((sum, d) => sum + d.tripCount, 0)}
                  </td>
                  <td className="py-3 text-right font-semibold">
                    {formatCurrency(supplierDebts.reduce((sum, d) => sum + d.totalValue, 0))} đ
                  </td>
                  <td className="py-3 text-right font-semibold text-green-600">
                    {formatCurrency(supplierDebts.reduce((sum, d) => sum + d.paidAmount, 0))} đ
                  </td>
                  <td className="py-3 text-right font-semibold text-orange-600">
                    {formatCurrency(totalSupplierDebt)} đ
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        )}

        {/* Customer Debts Table */}
        {activeTab === 'customer' && (
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 table-header">{t('debt.entityName')}</th>
                <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                <th className="pb-3 table-header text-right">
                  <div>{t('debt.totalValue')}</div>
                  <div className="text-xs font-normal text-blue-500">({t('trips.salePrice')})</div>
                </th>
                <th className="pb-3 table-header text-right">{t('debt.paidAmount')}</th>
                <th className="pb-3 table-header text-right">{t('debt.outstandingAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {customerDebts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    {t('common.noData')}
                  </td>
                </tr>
              ) : (
                customerDebts.map((debt) => (
                  <tr key={debt.id} className="border-b last:border-0">
                    <td className="py-3">
                      <button
                        onClick={() => navigate(`/debt/customers/${debt.id}`)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                        title={t('debt.viewDetails')}
                      >
                        {debt.name}
                      </button>
                    </td>
                    <td className="py-3 text-right">{debt.tripCount}</td>
                    <td className="py-3 text-right">{formatCurrency(debt.totalValue)} đ</td>
                    <td className="py-3 text-right text-green-600">
                      {formatCurrency(debt.paidAmount)} đ
                    </td>
                    <td className={`py-3 text-right font-semibold ${
                      debt.outstandingAmount > 0 ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {formatCurrency(debt.outstandingAmount)} đ
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {customerDebts.length > 0 && (
              <tfoot className="border-t bg-gray-50">
                <tr>
                  <td className="py-3 font-semibold">{t('common.total')}</td>
                  <td className="py-3 text-right font-semibold">
                    {customerDebts.reduce((sum, d) => sum + d.tripCount, 0)}
                  </td>
                  <td className="py-3 text-right font-semibold">
                    {formatCurrency(customerDebts.reduce((sum, d) => sum + d.totalValue, 0))} đ
                  </td>
                  <td className="py-3 text-right font-semibold text-green-600">
                    {formatCurrency(customerDebts.reduce((sum, d) => sum + d.paidAmount, 0))} đ
                  </td>
                  <td className="py-3 text-right font-semibold text-blue-600">
                    {formatCurrency(totalCustomerDebt)} đ
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
};
